import RenderManager from "../rendering/render-manager";
import MaterialBuilder from "../rendering/material-builder";
import AssetLibrary from "./asset-library";

let SPRITE_GEOMETRY = null;

class Spline extends THREE.Mesh {
    constructor() {
        const material = MaterialBuilder.build({ color: 0x000000 });
        const geometry = new THREE.BufferGeometry();

        const tmpPosition = new THREE.Float32BufferAttribute([], 3);
        geometry.addAttribute("position", tmpPosition);

        super(geometry, material);

        this._curve = null;
        this.isSpline = true;
        this.type = "Spline";

        this._flowAnim = null;
        this._controlPoints = new THREE.Group();
        this._controlPoints.visible = false;

        this.add(this._controlPoints);

        const camPosition = new THREE.Vector3(), worldPosition = new THREE.Vector3();
        const size = 0.2;
        this.onBeforeRender = (renderer, scene, camera, geometry, material, group) => {
            if (this._controlPoints.visible) {
                worldPosition.setFromMatrixPosition(this._controlPoints.matrixWorld);
                camPosition.setFromMatrixPosition(camera.matrixWorld);

                const scale = worldPosition.distanceTo(camPosition) / 6 * size * Math.tan(camera.fov / 180 * Math.PI);

                this._controlPoints.children.forEach(ch => ch.scale.set(scale, scale, scale));
            }
        }
    }

    get controlPoints() { return this._controlPoints.children; }
    get data() { return Q3.data.assets.objects[this.uuid]; }

    get controlPointsVisibility() { return this._controlPoints.visible; }
    set controlPointsVisibility(val) { this._controlPoints.visible = val; }

    updateSpline() {
        const data = this.data;

        this._curve = new THREE.CatmullRomCurve3(
            data.points.map(p => new THREE.Vector3(p.x, p.y, p.z)),
            data.closed,
            "catmullrom",
            data.tension);

        this.removeFlowAnimation();

        this.addCurveLine();

        if (data.flowAnim) {
            this.addFlowAnimation();
        }
    }

    get curve() { return this._curve; }

    removeFlowAnimation() { if (this._flowAnim) this.remove(this._flowAnim); }

    addFlowAnimation() {
        const data = this.data;

        Q3.flowAnimation = Q3.flowAnimation || {};

        if (data.flowAnim && data.flowAnim.sprite) {
            const flowObj = new THREE.Object3D('flow animation');

            flowObj.isSplineFlowAnim = true;
            flowObj.spline = this;
            flowObj.splineUuid = this.uuid;

            this.add(flowObj);

            flowObj.dontShowInHierarchyPanel = true;

            for (let i = 0, len = data.flowAnim.quantity; i < len; i++) {
                const element = this.createFlowElement();

                flowObj.add(element);
            }

            this._flowAnim = flowObj;
        }
    }

    createFlowElement() {
        const splineData = this.data;
        const spriteData = Q3.data.assets.objects[splineData.flowAnim.sprite];

        if (!spriteData) return;

        const spriteMaterialUuid = spriteData.material;
        const material = AssetLibrary.materials[spriteMaterialUuid];

        if (!material) return;

        if (!SPRITE_GEOMETRY) {
            SPRITE_GEOMETRY = new THREE.PlaneBufferGeometry(1, 1, 1, 1);
        }

        const sprite = new THREE.Mesh(SPRITE_GEOMETRY, material);
        sprite.renderOrder = this.renderOrder + 1;

        function setRotation(material, value) {
            if (material.uniforms && material.uniforms.rotation) {
                material.uniforms.rotation.value = value;
                material.uniformsNeedUpdate = true;
            }
        }

        sprite.onBeforeRender = (renderer, scene, camera, geometry, material, group) => {
            setRotation(material, sprite.spriteRotation);
        };

        sprite.onAfterRender = (renderer, scene, camera, geometry, material, group) => {
            setRotation(0);
        };

        sprite.dontShowInHierarchyPanel = true;
        sprite.isUnselectable = true;

        return sprite;
    }

    getThickness() { return this.data.thickness * 0.005; }

    updateGeometry() {
        const data = this.data, curve = this._curve;
        const thickness = this.getThickness();

        this.geometry.dispose();

        const lenCps = curve.points.length;

        if (lenCps === 0) return;

        const lenCurve = curve.getLength();

        let geometry = new THREE.TubeBufferGeometry(curve, Math.ceil(2 * lenCurve * lenCps), thickness, 8, data.closed);

        if (!data.closed) {
            const geoClosed1 = new THREE.SphereBufferGeometry(thickness, 32, 32);
            const p1 = curve.points[0];

            geoClosed1.translate(p1.x, p1.y, p1.z)

            const geoClosed2 = new THREE.SphereBufferGeometry(thickness, 32, 32);
            const p2 = curve.points[lenCps - 1];

            geoClosed2.translate(p2.x, p2.y, p2.z);

            geometry = THREE.BufferGeometryUtils.mergeBufferGeometries([geometry, geoClosed1, geoClosed2]);
        }

        this.geometry = geometry;
    }

    updateMaterial() {
        const data = this.data;
        this.material.color.copy(new THREE.Color(data.color));


        if (!data.showCurve) {
            if (Q3.editMode) {
                this.material.transparent = true;
                this.material.opacity = 0.25;
            } else {
                this.material.visible = false;
            }
        } else {
            this.material.transparent = false;
            this.material.opacity = 1;
        }
    }

    updateControlPoints() {
        const curve = this._curve;
        const thickness = this.getThickness();

        const currentCps = this._controlPoints.children;
        const desiredCps = curve.points;

        const lenCurrentCps = currentCps.length, lenDesiredCps = desiredCps.length;

        const dtCps = lenDesiredCps - lenCurrentCps;

        if (dtCps < 0) {
            this._controlPoints.children.slice(dtCps).forEach(ch => {
                this._controlPoints.remove(ch);
                RenderManager.raycaster.untrack(ch);
            });
        } else {
            for (let i = 0; i < dtCps; i++) {
                const cp = new ControlPoint(this);

                this._controlPoints.add(cp);

                RenderManager.raycaster.makeTrackable(cp);

                cp.renderOrder = this.renderOrder + 1;
                cp.material.transparent = true;
            }
        }

        const scale = 0.00001;
        curve.points.forEach((p, i) => {
            const cp = this._controlPoints.children[i];

            cp.scale.set(scale, scale, scale);
            cp.position.copy(p);

            cp.name = `ControlPoint_${i}`;
        });
    }

    addCurveLine() {
        this.updateGeometry();
        this.updateMaterial();
        this.updateControlPoints();
    }
}

export default Spline;
export { Spline };


export function updateSplines(time) {
    const viewSize = RenderManager.getSize();
    const aspect = viewSize.width / viewSize.height;
    const camera = RenderManager.activeCamera;


    RenderManager.activeScene.children.forEach(spline => {
        if (!spline.isSpline && spline.visible) return;

        spline.traverseVisible(o => {
            if (o.isSplineFlowAnim) {
                const splineData = Q3.data.assets.objects[o.splineUuid];
                if (!splineData || !splineData.flowAnim) return;
                const curve = o.spline.curve;
                let offset = time * (splineData.flowAnim.speed * splineData.flowAnim.speed);
                for (let i = 0; i < o.children.length; i++) {
                    const child = o.children[i];
                    const t = offset + i / o.children.length;
                    let p = t - Math.floor(t);
                    if (splineData.backwards) {
                        p = 1 - p;
                    }
                    let point0 = curve.getPoint(p);
                    child.position.copy(point0);

                    if (splineData.pathDirection) {
                        let point1 = curve.getPoint(p + 0.001);

                        point0.applyMatrix4(camera.matrixWorldInverse);
                        point0.applyMatrix4(camera.projectionMatrix);
                        point1.applyMatrix4(camera.matrixWorldInverse);
                        point1.applyMatrix4(camera.projectionMatrix);
                        child.spriteRotation = Math.atan2(point1.y - point0.y, (point1.x - point0.x) * aspect);
                    } else {
                        child.spriteRotation = 0;
                    }
                    child.spriteRotation += (splineData.spriteDirection || 0) * Math.PI / 180;
                }
                camera.update = true;
            }
        });
    });
}

class ControlPoint extends THREE.Mesh {
    static CONTROL_MATERIAL = null;
    static CONTROL_GEOMETRY = null;

    constructor(spline) {
        if (!ControlPoint.CONTROL_MATERIAL) {
            ControlPoint.CONTROL_MATERIAL = MaterialBuilder.build({ color: 0xffff00, depthTest: false, depthWrite: false, transparent: true });
        }
        if (!ControlPoint.CONTROL_GEOMETRY) {
            ControlPoint.CONTROL_GEOMETRY = new THREE.SphereBufferGeometry(1, 32, 32)
        }

        super(ControlPoint.CONTROL_GEOMETRY, ControlPoint.CONTROL_MATERIAL);

        this.isSplineControlPoint = true;
        this.type = "SplineControlPoint";
        this.spline = spline;
    }
}